"use client"

import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  ApolloLink,
  split
} from "@apollo/client"
import createUploadLink from "apollo-upload-client/createUploadLink.mjs"
import { createClient } from "graphql-ws"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { onError } from "@apollo/client/link/error"
import { getMainDefinition } from "@apollo/client/utilities"
import axios from "axios"
import { isAuthenticatedVar } from "store/auth"
import { getCookie } from "@/utils"
import { SUBSCRIPTION_COOKIE_NAME } from "app/_config/constants/common"

axios.defaults.withCredentials = true

// ==== ENDPOINT CONSTANTS ====
const BASE_GRAPHQL_URL = `${process.env.NEXT_PUBLIC_BASE_URL}/graphql`
const CMS_GRAPHQL_URL = `${process.env.NEXT_PUBLIC_CMS_URL}/graphql`
const CMS_API_TOKEN = process.env.NEXT_PUBLIC_CMS_API_TOKEN
const NOTIFICATIONS_GRAPHQL_URL = `${process.env.NEXT_PUBLIC_NOTIFICATIONS_BASE_URL}/subscriptions/graphql`
const NOTIFICATIONS_WS_GRAPHQL_URL = `${process.env.NEXT_PUBLIC_NOTIFICATIONS_BASE_WS_URL}/subscriptions/graphql`

export const ApolloWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  // Error handling link
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.message === "no valid tokens available") {
          window.location.href = "/auth/login"
          isAuthenticatedVar(false)
        }
      }
    }

    if (networkError && "statusCode" in networkError && networkError.statusCode === 401) {
      window.location.href = "/auth/login"
      isAuthenticatedVar(false)
    }
  })

  function makeClient() {
    // Middleware to dynamically set `uri`, `headers`, and `credentials`
    const dynamicLink = new ApolloLink((operation, forward) => {
      const apiName = operation.getContext().apiName as string

      let uri = BASE_GRAPHQL_URL
      let headers: Record<string, string> = {}
      let credentials: RequestCredentials = "include"

      if (apiName === "strapi") {
        uri = CMS_GRAPHQL_URL
        headers = { Authorization: `Bearer ${CMS_API_TOKEN}` }
        credentials = "omit"
      } else if (apiName === "notifications") {
        uri = NOTIFICATIONS_GRAPHQL_URL
        credentials = "include"
      }

      operation.setContext({
        uri,
        headers,
        fetchOptions: { credentials },
      })

      return forward(operation)
    })

    // This link handles both file uploads and normal JSON requests
    const uploadLink = createUploadLink({
      fetchOptions: { cache: "no-store" },
      credentials: "include",
    })

    // WebSocket link for subscriptions
    const wsLink = new GraphQLWsLink(
      createClient({
        url: NOTIFICATIONS_WS_GRAPHQL_URL,
        connectionParams: () => ({
          token: getCookie(SUBSCRIPTION_COOKIE_NAME),
        }),
      })
    )

    // Compose error, dynamic context, then uploadLink (terminating)
    const requestLink = ApolloLink.from([
      errorLink,
      dynamicLink,
      uploadLink,
    ])

    // Split: subscriptions → wsLink, everything else → HTTP
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query)
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        )
      },
      wsLink,
      requestLink
    )

    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache(),
      connectToDevTools: true,
    })
  }

  return <ApolloProvider client={makeClient()}>{children}</ApolloProvider>
}
